import { useCallback, useEffect, useRef } from 'react';
import { socketService } from '../services/socketService';
import { getClientType } from '../services/authService';
import { isTauri } from '../utils/tauri';

/**
 * 유휴 임계값 — 서버 `DESKTOP_IDLE_LIMIT_MS`(기본 5분)를 미러한다.
 *
 * ⚠ 이 값은 **판정이 아니라 보고 시점**만 정한다. 서버 env로 임계값을 바꿔도 클라이언트는
 *   최대 30초 안에 다시 보고하므로 자동으로 따라잡는다 — 값이 어긋나도 지연만 생기고
 *   판정이 갈라지지 않는다(판정 출처는 realtime.isDesktopSocketActive 하나).
 */
const AWAY_IDLE_MS = 5 * 60 * 1000;
/** 보고 주기 상한. 서버 `DESKTOP_ACTIVITY_TTL_MS`(90초)의 1/3 — 이 값을 늘리면 신선도 계약이 깨진다. */
const MAX_REPORT_INTERVAL_MS = 30000;
/** 자리비움에서 복귀했을 때 입력 이벤트가 쏟아져도 emit은 이 간격으로만 */
const RESUME_THROTTLE_MS = 1000;

/**
 * 데스크톱 활동 하트비트 — "PC에서 사용 중이면 모바일 푸시를 보내지 않는다"(Slack·Teams·Discord·카카오톡)와
 * **화면의 '자리 비움' 표시** 둘의 클라이언트 쪽 절반. `user:activity { idleMs, locked }`를 보내고
 * 판정은 전부 서버가 한다.
 *
 * 보고 시점이 세 갈래다:
 *  ① 자기 예약 타이머 — 다음 지연을 `AWAY_IDLE_MS - idleMs`로 잡되 1초~30초로 클램프한다.
 *     활동 중이면 30초(예전 고정 주기와 동일)고, 유휴 4분 50초면 10초 뒤 — 즉 **임계값을 넘는
 *     순간에 보고가 도착**한다. 고정 30초 주기로는 자리비움 표시가 최악 30초 늦었다.
 *  ② 복귀 즉시 — 마지막 보고가 임계값을 넘었을 때(=자리비움으로 보일 때)만 입력 발생 시 즉시 보고.
 *     없으면 마우스를 움직여도 최대 30초간 노란 점이 남는다.
 *  ③ 잠금 변화·창 포커스·소켓 재연결 — 아래 이벤트와 호출부의 sendNow.
 *
 * idleMs 출처:
 * - Tauri(Windows): Rust `get_system_idle_ms`(GetLastInputInfo) — PC 전체 입력을 본다. 트레이에 숨긴 채
 *   다른 프로그램을 쓰고 있어도 "PC 사용 중"이다. 구버전 바이너리(명령 없음)면 페이지 입력으로 폴백.
 * - 웹 데스크톱 브라우저: 이 페이지의 입력 이벤트만 볼 수 있다. 다른 탭/프로그램 사용은 유휴로 잡힌다(브라우저 한계).
 *
 * locked (Tauri Windows 전용): 화면 잠금·사용자 전환·RDP 연결 끊김. 유휴시간은 잠겨도 실시간으로만
 * 쌓여 서버 임계값(5분)을 기다려야 했지만, 잠금은 "사람이 PC 앞에 없다"가 확정된 순간이라 즉시 반영한다.
 * Rust `is_session_locked`가 상태를 돌려주고, Rust의 `session-lock-changed` 이벤트가 오면 30초를
 * 기다리지 않고 즉시 다시 보고한다. 엣지가 아니라 '상태'로 매번 실어 보내므로 이벤트 유실·소켓
 * 재연결·잠긴 상태에서의 업데이터 재시작에 자가 치유된다. 웹은 OS 잠금을 알 수 없어 항상 false.
 *
 * 모바일 클라이언트(`getClientType() === 'mobile'`)는 아무것도 보내지 않는다 — 서버도 desktop 세션만 본다.
 *
 * @param enabled 로그인 상태에서만 true
 * @returns sendNow — 창 포커스 복귀·소켓 (재)연결 직후처럼 30초를 기다리지 않고 즉시 보고할 때 호출 (참조 안정)
 */
export function useDesktopActivityHeartbeat(enabled: boolean): () => void {
  const lastInputAtRef = useRef<number>(Date.now());
  // 마지막으로 보고한 idleMs가 임계값을 넘었는지 — 복귀 즉시 보고(②)의 게이트.
  // '지금 유휴인가'가 아니라 '서버가 나를 자리비움으로 보고 있는가'를 뜻한다.
  const reportedAwayRef = useRef(false);
  const lastResumeSentAtRef = useRef(0);
  // 다음 보고 예약. sendNow가 스스로 다시 잡는다(고정 interval이 아니다).
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isDesktop = getClientType() === 'desktop';
  const active = enabled && isDesktop;

  const sendNow = useCallback(() => {
    if (!active) return;

    // 보고한 idleMs로 다음 보고 시점을 정한다 — 임계값을 넘는 순간에 도착하도록.
    const schedule = (idleMs: number) => {
      reportedAwayRef.current = idleMs >= AWAY_IDLE_MS;
      if (timerRef.current) clearTimeout(timerRef.current);
      const remaining = AWAY_IDLE_MS - idleMs;
      const delay = Math.min(MAX_REPORT_INTERVAL_MS, Math.max(1000, remaining));
      timerRef.current = setTimeout(sendNow, delay);
    };

    const report = (idleMs: number, locked: boolean) => {
      socketService.emitActivity(idleMs, locked);
      // 잠금 중에는 유휴가 실시간으로만 쌓여 남은 시간이 길게 잡히지만, 서버가 잠금을 즉시
      // 자리비움으로 판정하므로 그대로 둔다(이미 away라 조급하게 다시 보고할 이유가 없다).
      schedule(idleMs);
    };

    const pageIdleMs = Math.max(0, Date.now() - lastInputAtRef.current);
    if (isTauri()) {
      import('@tauri-apps/api/core')
        .then(({ invoke }) =>
          // 개별 catch: 둘 중 하나가 없는(dev 핫리로드 등) 바이너리여도 나머지 하나는 살린다.
          Promise.all([
            invoke<number>('get_system_idle_ms').catch(() => null),
            invoke<boolean>('is_session_locked').catch(() => null),
          ])
        )
        .then(([osIdleMs, locked]) => {
          const idle = typeof osIdleMs === 'number' && Number.isFinite(osIdleMs) && osIdleMs >= 0
            ? osIdleMs
            : pageIdleMs;
          // null(명령 없음/실패) → false. 잠금을 모르는 쪽이 기존 동작이다.
          report(idle, locked === true);
        })
        .catch(() => report(pageIdleMs, false));
      return;
    }
    report(pageIdleMs, false);
  }, [active]);

  useEffect(() => {
    if (!active) return;

    // 페이지 입력 추적 — 리스너는 ref 대입만 하므로 mousemove를 포함해도 비용이 없다.
    // 자리비움으로 보이는 동안에만 즉시 보고를 얹는다(1초 스로틀). 평소에는 예전처럼 ref 대입만.
    const touch = () => {
      const now = Date.now();
      lastInputAtRef.current = now;
      if (!reportedAwayRef.current) return;
      if (now - lastResumeSentAtRef.current < RESUME_THROTTLE_MS) return;
      lastResumeSentAtRef.current = now;
      sendNow();
    };
    const events: Array<keyof WindowEventMap> = ['pointerdown', 'keydown', 'wheel', 'touchstart', 'mousemove'];
    events.forEach((ev) => window.addEventListener(ev, touch, { passive: true, capture: true }));

    lastInputAtRef.current = Date.now();
    sendNow();

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = null;
      events.forEach((ev) => window.removeEventListener(ev, touch, { capture: true }));
    };
  }, [active, sendNow]);

  // 잠금/해제 순간 즉시 재보고. 30초 하트비트를 기다리면 잠근 직후 도착한 메시지가 폰으로 안 간다.
  // 페이로드는 쓰지 않고 sendNow가 is_session_locked를 다시 읽는다 — Rust 원자값이 emit 전에
  // 갱신되므로 값이 어긋날 수 없고, 판정 출처가 한 곳으로 남는다.
  useEffect(() => {
    if (!active || !isTauri()) return;
    let unlisten: (() => void) | null = null;
    let cancelled = false;
    import('@tauri-apps/api/event')
      .then(({ listen }) => listen<boolean>('session-lock-changed', () => { sendNow(); }))
      .then((un) => {
        // 등록이 끝나기 전에 cleanup이 돌았으면 바로 해제한다(리스너 유실 방지)
        if (cancelled) { un(); return; }
        unlisten = un;
      })
      .catch(() => {});
    return () => {
      cancelled = true;
      unlisten?.();
    };
  }, [active, sendNow]);

  return sendNow;
}
